import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isToday,
  isSameMonth,
} from 'date-fns';
import { PlannerPlan } from '@/lib/types';
import { SCOPE_DOT_COLOR } from '@/pages/planner/plan-scope';

interface MonthGridProps {
  anchor: Date;
  selectedDate: string | null;
  plansByDate: Record<string, PlannerPlan[]>;
  onSelectDate: (date: string) => void;
}

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Month view: 42-cell padded grid (6 weeks x 7 days), a plan name chip per
// day-slot in the cell (scope-colored leading dot: violet workspace, brand
// "My Plan"), today = filled circle on the day number, out-of-month days
// dimmed.
export default function MonthGrid({ anchor, selectedDate, plansByDate, onSelectDate }: MonthGridProps) {
  const monthStart = startOfMonth(anchor);
  const monthEnd = endOfMonth(anchor);
  const gridStart = startOfWeek(monthStart);
  const gridEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  return (
    <Box>
      <SimpleGrid columns={7} mb={2} textAlign='center'>
        {DOW_LABELS.map((label) => (
          <Text key={label} fontSize='xs' fontWeight='600' textTransform='uppercase' color='fg.muted'>
            {label}
          </Text>
        ))}
      </SimpleGrid>

      <SimpleGrid columns={7} gap={1}>
        {days.map((day) => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const isSelected = selectedDate === dateStr;
          const isCurrentDay = isToday(day);
          const inMonth = isSameMonth(day, anchor);
          const dayPlans = plansByDate[dateStr] ?? [];

          return (
            <Box
              key={dateStr}
              as='button'
              type='button'
              onClick={() => onSelectDate(dateStr)}
              display='flex'
              flexDirection='column'
              alignItems='center'
              gap={1}
              minH='72px'
              py={2}
              px={1}
              borderRadius='md'
              borderWidth='1px'
              borderColor={isSelected ? 'brand.solid' : 'border'}
              bg={isSelected ? 'brand.solid' : 'bg.panel'}
              opacity={inMonth ? 1 : 0.4}
              cursor='pointer'
              transition='background-color .12s, color .12s, border-color .12s'
              _hover={isSelected ? undefined : { borderColor: 'border.emphasized' }}
            >
              <Box
                display='flex'
                alignItems='center'
                justifyContent='center'
                w='22px'
                h='22px'
                borderRadius='full'
                bg={isCurrentDay && !isSelected ? 'brand.solid' : 'transparent'}
              >
                <Text
                  fontFamily='heading'
                  fontWeight='800'
                  fontSize='sm'
                  color={isSelected || isCurrentDay ? 'brand.contrast' : 'fg'}
                >
                  {format(day, 'd')}
                </Text>
              </Box>
              {dayPlans.map((plan) => (
                <Flex
                  key={plan.scope}
                  align='center'
                  gap={1}
                  px={1}
                  borderRadius='sm'
                  bg={isSelected ? 'brand.contrast' : 'brand.subtle'}
                  w='full'
                  minW={0}
                >
                  <Box
                    w='4px'
                    h='4px'
                    borderRadius='full'
                    bg={SCOPE_DOT_COLOR[plan.scope]}
                    flexShrink={0}
                  />
                  <Text
                    fontSize='label'
                    lineClamp={1}
                    color={isSelected ? 'brand.solid' : 'fg'}
                    minW={0}
                  >
                    {plan.name}
                  </Text>
                </Flex>
              ))}
            </Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}
